import * as React from "react";
import moment from "moment";
import clsx from "clsx";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { FindUserPopup } from "../user/FindUserPopup";
import _ from "lodash";
import { Partnership } from "../../service/api-response/PartnershipListSuccessResponse";
import { PromotionCodeScope } from "../../model/PromotionCodeScope";
import * as organizationSelectors from "../organization/organizationSelectors";
import * as naturalSort from "node-natural-sort";
import { VoucherTypeListItem } from "../shop/model/GetVoucherTypeListSuccessResponse";
import { useSelector } from "react-redux";
import * as Inputs from "../common/hook-form/index";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons";
import { FormRow } from "../common/hook-form/index";
import { PromotionCodeFormType } from "./model/PromotionCodeSuccessResponse";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { CheckboxInput } from "../../components/forms/inputs/CheckboxInput";

interface Props {
  onSubmit: Function;
  isUpserting: boolean;
  partnerships: undefined | Partnership[];
  voucherTypes: undefined | VoucherTypeListItem[];
  promotionCode: undefined | any;
}

const PromotionCodeUpsertForm: React.FC<Props> = (props) => {
  const [
    isAutocompleteUserPopupVisible,
    setIsAutocompleteUserPopupVisible,
  ] = React.useState(false);

  const methods = useForm<PromotionCodeFormType>({
    resolver: yupResolver(promotionCodeSchema),
  });
  const {
    handleSubmit,
    register,
    reset,
    watch,
    getValues,
    setValue,
    control,
    formState: { errors: formErrors },
    clearErrors,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "voucherTypes",
  });

  const isEdit = !!props.promotionCode;
  const benefitTypes = {
    voucher: "Vouchers",
    membercard: "Member card",
  };

  const partnershipIdFormField = watch("partnershipId");
  const [selectedBenefitType, setSelectedBenefitType] = React.useState<
    string | undefined
  >(undefined);
  const isMemberCardChosen = selectedBenefitType === benefitTypes.membercard;
  const selectedBenefitTypeInitial =
    partnershipIdFormField || isMemberCardChosen
      ? benefitTypes.membercard
      : benefitTypes.voucher;

  React.useEffect(() => {
    setSelectedBenefitType(selectedBenefitTypeInitial);
  }, [selectedBenefitTypeInitial]);

  const isVoucherVisibleOnPromotionCode = useSelector(
    organizationSelectors.isVoucherVisibleOnPromotionCode
  );

  const comparator = React.useMemo(
    () => naturalSort({ caseSensitive: false }),
    []
  );

  const onSubmit = (values: PromotionCodeFormType) => {
    const vals = { ...values };
    vals.mustBeTrialPeriodEligible = false;
    vals.validFrom = moment(values.validFrom).format();
    vals.validTo = moment(values.validTo).format();

    if (isVoucherVisibleOnPromotionCode) {
      vals.voucherTypes = _.filter(
        vals.voucherTypes,
        (x) => x.id && x.amount
      ) as {
        id: string;
        amount: number;
      }[];
    }

    if (vals.userId === "") {
      vals.userId = null;
    }

    if (vals.partnershipId === "") {
      vals.partnershipId = null;
    }
    vals.scope = PromotionCodeScope.Redeem;
    props.onSubmit(vals);
  };

  const openUserSearchPopup = () => setIsAutocompleteUserPopupVisible(true);
  const closeUserSearchPopup = () => setIsAutocompleteUserPopupVisible(false);
  const partnerships = props.partnerships;
  const voucherTypes = props.voucherTypes;

  const promotionCode = props.promotionCode;

  React.useEffect(() => {
    if (isEdit && promotionCode) {
      reset({
        ...promotionCode,
        validTo: promotionCode.validTo
          ? moment(promotionCode.validTo).format("YYYY-MM-DDTHH:mm:ss")
          : null,
        validFrom: promotionCode.validFrom
          ? moment(promotionCode.validFrom).format("YYYY-MM-DDTHH:mm:ss")
          : null,
      });
    }
  }, [isEdit, promotionCode, reset]);

  let voucherOptions = React.useMemo(() => {
    const options = (voucherTypes || []).map((x) => ({
      label: x.name,
      value: x.id,
    }));

    const sorted = options.sort((a, b) => comparator(a.label, b.label));

    sorted.unshift({
      label: "",
      value: "",
    });

    return sorted;
  }, [voucherTypes, comparator]);

  const partnershipOptions = React.useMemo(() => {
    let options = (partnerships || []).map((x) => ({
      label: `${x.pool} (${x.memberCardTypeName})`,
      value: x.id,
    }));

    const sorted = options.sort((a, b) => comparator(a.label, b.label));

    sorted.unshift({
      label: "",
      value: "",
    });

    return sorted;
  }, [partnerships, comparator]);

  const scopeValue = watch("scope", getValues()["scope"]);

  return (
    <FormProvider {...methods}>
      {isAutocompleteUserPopupVisible && (
        <FindUserPopup
          onUserSelect={(userId, name) => {
            setValue("userId", userId);
            closeUserSearchPopup();
          }}
          onClose={() => closeUserSearchPopup()}
        />
      )}

      <form className="card" onSubmit={handleSubmit(onSubmit)}>
        <div className="card-header">
          <StyledText
            color={colors.textPrimary}
            className="bodyHeaderOneRegular card-title"
          >
            {isEdit ? "Edit promotion code" : "Create new promotion code"}
          </StyledText>
        </div>
        <div className="card-body">
          <div>
            <StyledText
              color={colors.textPrimary}
              className="bodyHeaderOneSemi mb-2"
            >
              Code redeem benefit
            </StyledText>
            <StyledText
              color={colors.textPrimary}
              className="bodyTextRegular mb-4"
            >
              {!isEdit
                ? "Choose with type of benefit you want to distribute through this code. If you choose vouchers, the user will receive vouchers if the user enters the code in the app. Vice versa with member card. Note that you cannot combine the two."
                : "Edit the benefits the user receives once entering the code below. Note that you cannot combine the two."}
            </StyledText>
          </div>
          <div
            className="d-flex flex-row"
            style={{ gap: "16px", marginBottom: "32px" }}
          >
            <SecondaryButton
              backgroundColor={
                selectedBenefitType === benefitTypes.voucher
                  ? colors.ctaLight
                  : colors.white
              }
              color={
                selectedBenefitType === benefitTypes.voucher
                  ? colors.ctaMain
                  : colors.textPrimary
              }
              hoverColor={colors.ctaLight}
              onClick={() => setSelectedBenefitType(benefitTypes.voucher)}
              type="button"
            >
              {benefitTypes.voucher}
            </SecondaryButton>
            <SecondaryButton
              backgroundColor={
                selectedBenefitType === benefitTypes.membercard
                  ? colors.ctaLight
                  : colors.white
              }
              color={
                selectedBenefitType === benefitTypes.membercard
                  ? colors.ctaMain
                  : colors.textPrimary
              }
              hoverColor={colors.ctaLight}
              onClick={() => setSelectedBenefitType(benefitTypes.membercard)}
              type="button"
            >
              {benefitTypes.membercard}
            </SecondaryButton>
          </div>
          <div>
            <StyledText
              color={colors.textPrimary}
              className="bodyHeaderOneSemi mb-2"
            >
              Standard promotion code information
            </StyledText>
            <StyledText
              color={colors.textPrimary}
              className="bodyTextRegular mb-4"
            >
              This is the standard information required to create a new
              promotion code. You cannot create the code if this information is
              not filled in.
            </StyledText>
          </div>
          <FormRow>
            <Inputs.InputFormField
              errors={formErrors}
              register={register}
              label="Code"
              name="code"
              helpText="The code a user must type in to get the benefit. Please note, users can type in the code using both small and big letters and the code will still work. Therefore, you can use big and small letters to “design” the code for better readability."
            />
            <Inputs.InputFormField
              errors={formErrors}
              register={register}
              label="Redeem Limit"
              name="redeemLimit"
              helpText="The amount of times this code can be used. Please note, a code can only be used one time per specific user."
            />
          </FormRow>
          <FormRow>
            <Inputs.InputFormField
              errors={formErrors}
              register={register}
              label="Valid from"
              name="validFrom"
              step="1"
              type="datetime-local"
              helpText="When is the code active from?"
            />
            <Inputs.InputFormField
              errors={formErrors}
              register={register}
              label="Valid To"
              name="validTo"
              step="1"
              type="datetime-local"
              helpText="When is the last day a user can use the code?"
            />
          </FormRow>
          <div className="mt-2">
            <StyledText
              color={colors.textPrimary}
              className="bodyHeaderOneSemi mb-2"
            >
              Internal notes
            </StyledText>
            <StyledText
              color={colors.textPrimary}
              className="bodyTextRegular mb-4"
            >
              Internal description. Only visible for admins.
            </StyledText>
          </div>
          {scopeValue === PromotionCodeScope.Subscription ? (
            // @ts-ignore
            <Inputs.TextareaFormField
              errors={formErrors}
              register={register}
              label="Internal note"
              name="note"
            />
          ) : (
            <Inputs.TextareaFormField
              errors={formErrors}
              register={register}
              label="Internal note"
              name="note"
            />
          )}
          <div className="d-flex flex-row justify-content-between align-items-center w-100 mt-6">
            <div>
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi mb-2"
              >
                {selectedBenefitType === benefitTypes.voucher
                  ? benefitTypes.voucher
                  : benefitTypes.membercard}
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-4"
              >
                {selectedBenefitType === benefitTypes.voucher
                  ? "Select the vouchers the user will receive when entering the code. You can create vouchers in the tab Voucher types."
                  : "Select the membercard the user will receive when entering the code. You can create member cards in the tab Member card types."}
              </StyledText>
            </div>
            {selectedBenefitType === benefitTypes.voucher && (
              <PrimaryButton
                color={colors.errorDark}
                className="ml-2"
                backgroundColor={colors.white}
                hoverColor={colors.errorLight}
                type="button"
                onClick={() => setValue("voucherTypes", [])}
              >
                Clear voucher choices
              </PrimaryButton>
            )}
          </div>
          {isVoucherVisibleOnPromotionCode &&
            selectedBenefitType === benefitTypes.voucher && (
              <>
                {/* @ts-ignore */}
                {fields.map((field, index) => (
                  <FormRow key={field.id}>
                    <SelectInput
                      label={`Voucher ${index + 1}`}
                      name={`voucherTypes.${index}.id`}
                      options={voucherOptions}
                    />
                    <TextInput
                      label="Amount"
                      name={`voucherTypes.${index}.amount`}
                      type="number"
                    />
                    <PrimaryButton
                      color={colors.errorMedium}
                      className="ml-2 mt-4"
                      backgroundColor={colors.white}
                      hoverColor={colors.ctaLight}
                      type="button"
                      onClick={() => remove(index)}
                    >
                      Remove
                    </PrimaryButton>
                  </FormRow>
                ))}
                <PrimaryButton
                  color={colors.textPrimary}
                  backgroundColor={colors.white}
                  hoverColor={colors.ctaLight}
                  type="button"
                  onClick={() =>
                    append({
                      id: "",
                      amount: 0,
                    })
                  }
                >
                  + Add voucher
                </PrimaryButton>
              </>
            )}
          {selectedBenefitType === benefitTypes.membercard && (
            <Inputs.SelectFormField
              errors={formErrors}
              register={register}
              label="Member card"
              name="partnershipId"
              options={partnershipOptions}
              clearErrors={clearErrors}
              helpText="Add member card the user will receive when redeeming the code. If you have created a member card type and cannot find it in the list you probably need to add a partnership to it. Go to the tab Partnerships and add the pool onlinePos to the member card."
            />
          )}
          <div className="mt-6">
            <StyledText
              color={colors.textPrimary}
              className="bodyHeaderOneSemi mb-2"
            >
              Advanced promotion code information
            </StyledText>
            <StyledText
              color={colors.textPrimary}
              className="bodyTextRegular mb-4"
            >
              Below is the advanced information for promotion codes. You do not
              have to fill this out in order to create a promotion code.
            </StyledText>
          </div>
          {isVoucherVisibleOnPromotionCode &&
            scopeValue === PromotionCodeScope.Subscription && (
              <CheckboxInput
                label="Must be first customer card. When enabling this user can only use the code if he/she has not added a credit card before"
                name="mustBeFirstCustomerCard"
              />
            )}
          <div
            className={clsx({
              "form-group": true,
              "has-error": !!formErrors?.["userId"],
            })}
          >
            <StyledText
              color={colors.textPrimary}
              className="bodySubtextSemibold mb-2"
            >
              User ID
            </StyledText>
            <div className="input-group">
              <input
                className="form-control"
                type="text"
                {...register("userId")}
              />
              <PrimaryButton
                color={colors.textPrimary}
                className="ml-2"
                backgroundColor={colors.white}
                hoverColor={colors.ctaLight}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  openUserSearchPopup();
                }}
              >
                Search for user
              </PrimaryButton>
            </div>

            {!!formErrors?.["userId"] && (
              <span className="form-text text-danger">
                {formErrors?.["userId"]?.message}
              </span>
            )}
          </div>
        </div>
        <div className="card-footer d-flex justify-content-end">
          <PrimaryButton
            color="white"
            backgroundColor={colors.ctaMain}
            hoverColor={colors.ctaSecondaryOne}
            disabled={props.isUpserting}
            type="submit"
          >
            {props.isUpserting ? "Saving..." : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </FormProvider>
  );
};

const voucherTypeSchema = {
  id: yup.string().required("Please select voucher"),
  amount: yup
    .number()
    .required()
    .typeError("Required")
    .min(1, "Must be positive number")
    .max(10, "Cannot be more than 10"),
};

const promotionCodeSchema = yup.object().shape({
  code: yup.string().required("This field is required"),
  validFrom: yup
    .string()
    .test("Date validity test", "Invalid date", (value) =>
      moment(value).isValid()
    )
    .required("This field is required"),
  validTo: yup
    .string()
    .test("Date validity test", "Invalid date", (value) =>
      moment(value).isValid()
    )
    .required("This field is required"),
  voucherTypes: yup.array().of(yup.object().shape(voucherTypeSchema)),
});

export default PromotionCodeUpsertForm;
